import React, { useEffect, useState } from 'react';
import { adminListAllFeatureGroups, adminCreateFeatureGroup } from '../services/featureService';
import './AdminFeatureGroups.css';

const AdminFeatureGroups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminListAllFeatureGroups();
      setGroups(data);
    } catch (err) {
      setError(err.message || 'Failed to load feature maps');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newGroupName.trim()) {
      setError('Feature Map name is required');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      await adminCreateFeatureGroup(newGroupName.trim());
      setNewGroupName('');
      setShowCreateDialog(false);
      await loadGroups();
    } catch (err) {
      setError(err.message || 'Failed to create feature map');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="admin-feature-groups">
      <div className="admin-feature-groups-header">
        <button
          type="button"
          className="create-feature-map-button"
          onClick={() => setShowCreateDialog(true)}
          disabled={loading}
        >
          <i className="fas fa-plus"></i> Create Feature Map
        </button>
      </div>

      {showCreateDialog && (
        <div className="create-dialog-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="create-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="create-dialog-header">
              <h3>Create Feature Map</h3>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewGroupName('');
                  setError(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <form onSubmit={handleCreate} className="create-dialog-form">
              <div className="form-group">
                <label htmlFor="feature-map-name">
                  Feature Map Name <span className="required">*</span>
                </label>
                <input
                  id="feature-map-name"
                  type="text"
                  value={newGroupName}
                  onChange={(e) => {
                    setNewGroupName(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter feature map name"
                  disabled={creating}
                  required
                  autoFocus
                />
              </div>

              <div className="create-dialog-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewGroupName('');
                    setError(null);
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Create
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && <div className="admin-feature-groups-loading">Loading feature maps...</div>}
      {error && !showCreateDialog && <div className="admin-feature-groups-error">{error}</div>}
      {!loading && !error && (
        <table className="admin-feature-groups-table">
          <thead>
            <tr>
              <th>Feature Map Name</th>
              <th>Owner</th>
              <th>Last Updated</th>
              <th>Feature Count</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(group => (
              <tr key={group.feature_group_id}>
                <td>{group.display_name}</td>
                <td>
                  <div className="owner-info">
                    <span>{group.owner_name || 'Unknown'}</span>
                    {group.owner_email && (
                      <span className="owner-email">{group.owner_email}</span>
                    )}
                  </div>
                </td>
                <td>{group.updated_at ? new Date(group.updated_at).toLocaleString() : 'N/A'}</td>
                <td>{group.feature_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminFeatureGroups;

